class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;
    this.observer = null;
    this.loadingElement = null;
    this.isEnabled = true; // Always enabled
    this.batchSize = parseInt(this.dataset.batchSize, 10) || 5;
    this.pagesLoadedInBatch = 0;
    this.loadMoreButton = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  initialize() {
    this.createLoadingElement();
    this.createLoadMoreButton();
    this.setupIntersectionObserver();
    this.bindEvents();
    this.enableInfiniteScroll();
  }

  createLoadingElement() {
    this.loadingElement = document.createElement('div');
    this.loadingElement.className = 'infinite-scroll-loading hidden';
    this.loadingElement.innerHTML = `
      <div class="infinite-scroll-loading__spinner">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="31.416" stroke-dashoffset="31.416">
            <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
            <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
      <span class="infinite-scroll-loading__text">Loading more products...</span>
    `;
    const productGrid = document.getElementById('product-grid') || document.querySelector('.template-search__results');
    if (productGrid) {
      const container = productGrid.closest('.collection') || productGrid.parentNode;
      container.insertBefore(this.loadingElement, productGrid.nextSibling);
    }
  }

  createLoadMoreButton() {
    this.loadMoreButton = document.createElement('button');
    this.loadMoreButton.className = 'infinite-scroll-load-more button button--primary';
    this.loadMoreButton.textContent = 'Load more products';
    this.loadMoreButton.style.display = 'none';
    const productGrid = document.getElementById('product-grid') || document.querySelector('.template-search__results');
    if (productGrid) {
      const container = productGrid.closest('.collection') || productGrid.parentNode;
      container.insertBefore(this.loadMoreButton, this.loadingElement ? this.loadingElement.nextSibling : productGrid.nextSibling);
    }
    this.loadMoreButton.addEventListener('click', () => {
      this.pagesLoadedInBatch = 0;
      this.loadMoreButton.style.display = 'none';
      this.observeLastProduct();
    });
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.isEnabled && !this.isLoading && this.hasMorePages) {
          if (this.pagesLoadedInBatch < this.batchSize) {
            this.loadNextPage();
          } else {
            this.loadMoreButton.style.display = 'block';
            this.stopObserving();
          }
        }
      });
    }, options);
  }

  bindEvents() {
    document.addEventListener('facetFiltersForm:updated', () => {
      this.resetInfiniteScroll();
    });
    document.addEventListener('facetFiltersForm:rendered', () => {
      this.resetInfiniteScroll();
    });
    const observer = new MutationObserver(() => {
      if (!this.loadingElement && (document.getElementById('product-grid') || document.querySelector('.template-search__results'))) {
        this.createLoadingElement();
      }
      if (!this.loadMoreButton && (document.getElementById('product-grid') || document.querySelector('.template-search__results'))) {
        this.createLoadMoreButton();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  enableInfiniteScroll() {
    const paginationWrapper = document.querySelector('.pagination-wrapper');
    if (paginationWrapper) {
      paginationWrapper.style.display = 'none';
    }
    
    // Add class to indicate infinite scroll is active
    const productGrid = document.getElementById('product-grid') || document.querySelector('.template-search__results');
    if (productGrid) {
      productGrid.classList.add('infinite-scroll-active');
    }
    
    this.pagesLoadedInBatch = 0;
    this.loadMoreButton.style.display = 'none';
    this.observeLastProduct();
  }

  observeLastProduct() {
    const productGrid = document.getElementById('product-grid') || document.querySelector('.template-search__results');
    if (!productGrid) return;
    
    const productList = productGrid.tagName === 'UL' ? productGrid : productGrid.querySelector('ul');
    if (!productList) return;
    
    const products = productList.querySelectorAll('.grid__item, li');
    if (products.length > 0) {
      this.observer.observe(products[products.length - 1]);
    } else if (this.loadingElement) {
      this.observer.observe(this.loadingElement);
    }
  }

  stopObserving() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  async loadNextPage() {
    if (this.isLoading || !this.hasMorePages) return;
    this.isLoading = true;
    this.showLoading();
    try {
      const nextPage = this.currentPage + 1;
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('page', nextPage);
      currentUrl.searchParams.set('section_id', this.getSectionId());
      const response = await fetch(currentUrl.toString());
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newPagination = doc.querySelector('.pagination-wrapper');
      if (!newPagination || newPagination.children.length === 0) {
        this.hasMorePages = false;
        this.hideLoading();
        this.isLoading = false;
        this.loadMoreButton.style.display = 'none';
        return;
      }

      const newProductGrid = doc.getElementById('product-grid') || doc.querySelector('.template-search__results');
      if (!newProductGrid) return;
      
      const newProductList = newProductGrid.tagName === 'UL' ? newProductGrid : newProductGrid.querySelector('ul');
      const newProducts = newProductList ? newProductList.querySelectorAll('.grid__item, li') : [];
      const currentProductGrid = document.getElementById('product-grid') || document.querySelector('.template-search__results');
      
      if (currentProductGrid && newProducts.length > 0) {
        const currentProductList = currentProductGrid.tagName === 'UL' ? currentProductGrid : currentProductGrid.querySelector('ul');
        newProducts.forEach((product, index) => {
          // Skip featured product promos in infinite scroll loads
          if (product.classList.contains('featured-product-promo-js')) {
            // This is a featured product promo - skip it in infinite scroll
            return;
          }
          
          const images = product.querySelectorAll('img[loading="lazy"]');
          images.forEach(img => {
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
          });
          currentProductList.appendChild(product.cloneNode(true));
        });
        this.updateProductCount(doc);
        this.currentPage = nextPage;
        this.pagesLoadedInBatch++;
        if (this.pagesLoadedInBatch < this.batchSize) {
          this.stopObserving();
          this.observeLastProduct();
        } else {
          this.loadMoreButton.style.display = 'block';
          this.stopObserving();
        }
        if (typeof initializeScrollAnimationTrigger === 'function') {
          const newProductsHtml = Array.from(newProducts).map(p => p.outerHTML).join('');
          initializeScrollAnimationTrigger(newProductsHtml);
        }
      }
    } catch (error) {
      console.error('Error loading next page:', error);
    } finally {
      this.hideLoading();
      this.isLoading = false;
    }
  }

  updateProductCount(doc) {
    const newCount = doc.getElementById('ProductCount');
    const currentCount = document.getElementById('ProductCount');
    const currentCountDesktop = document.getElementById('ProductCountDesktop');
    if (newCount && currentCount) {
      currentCount.innerHTML = newCount.innerHTML;
    }
    if (newCount && currentCountDesktop) {
      currentCountDesktop.innerHTML = newCount.innerHTML;
    }
  }

  showLoading() {
    if (this.loadingElement) {
      this.loadingElement.classList.remove('hidden');
    }
  }

  hideLoading() {
    if (this.loadingElement) {
      this.loadingElement.classList.add('hidden');
    }
  }

  resetInfiniteScroll() {
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;
    this.pagesLoadedInBatch = 0;
    this.loadMoreButton.style.display = 'none';
    this.stopObserving();
    
    // Remove infinite scroll active class
    const productGrid = document.getElementById('product-grid') || document.querySelector('.template-search__results');
    if (productGrid) {
      productGrid.classList.remove('infinite-scroll-active');
    }
    
    if (this.isEnabled) {
      this.observeLastProduct();
    }
  }

  getSectionId() {
    const productGrid = document.getElementById('product-grid') || document.querySelector('.template-search__results');
    return productGrid ? productGrid.dataset.id : null;
  }
}

customElements.define('infinite-scroll', InfiniteScroll); 